import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../UI/dialog';
import { Button } from '../UI/button';
import { Label } from '../UI/label';
import { ArrowLeft, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import CustomSearchableDropdown from '../Common/CustomSearchableDD';
import { useSelector } from 'react-redux';
import { getThreadTypesArr } from '../../Store/Reducers';
import { getThreadNameFromTypeID } from '../../Utils/Utilities';
import { Checkbox } from '../UI/checkbox';

const CancelThreadDialog = ({
  open,
  onOpenChange,
  onConfirm,
  typeID = 2,
  currStageID = 1,
  rejectionReasonOptions = [],
  disqlRsns = [],
  requireReason = true,
  onAddReasonClick,
  connID = 0,
}) => {
  const [selectedReason, setSelectedReason] = useState("");
  const [isMarkCold, setIsMarkCold] = useState(false);
  const threadTypesData = useSelector(getThreadTypesArr);
  const { data: threadData } = useSelector((state) => state.threadData);

  // Determine current type category
  const currThrType = threadTypesData.find(type => type.TID === typeID);
  const isSalesType = currThrType?.Flow === "In";
  const isPurchaseType = currThrType?.Flow === "Out";
  const isInquiry = typeID === 1 || typeID === 4;

  // Determine which reason type to show based on stage and type
  const showDisqlReason = (typeID == 1 && currStageID === 1) || (isSalesType && typeID != 1);
  const showRejectionReason = (typeID == 1 && currStageID > 1) || isPurchaseType;

  // Transform disqlRsns to match the expected format
  const disqlRsnsOptions = disqlRsns.map(reason => ({
    value: reason.RID,
    label: reason.R,
  }));

  const openInquiriesCount = threadData?.filter(thread =>
    thread.ConnID === connID &&
    thread.TypeID === 1 &&
    thread.StatusID == 0
  ).length || 0;

  const showMarkAsCold = selectedReason && openInquiriesCount === 1;

  useEffect(() => {
    if (!open) {
      setSelectedReason("");
      setIsMarkCold(false);
    }
  }, [open]);

  const handleAddReason = (type, searchText) => {
    if (onAddReasonClick) {
      onAddReasonClick(type, searchText);
    }
  };

  const handleCancel = () => {
    setSelectedReason("");
    onOpenChange(false);
  };

  const handleConfirm = () => {
    // For sales types, validate reason selection
    if (requireReason) {
      if (!selectedReason) {
        const reasonType = showDisqlReason ? "disqualification" : "failure";
        toast.error(`Please select a ${reasonType} reason.`);
        return;
      }

      // Get the selected reason text based on reason type
      let reasonText = "";
      if (showDisqlReason) {
        const selectedReasonObj = disqlRsns.find(reason => reason.RID === selectedReason);
        reasonText = selectedReasonObj ? selectedReasonObj.R : 'Marked as Fake';
      } else if (showRejectionReason) {
        const selectedReasonObj = rejectionReasonOptions.find(option => option.value === selectedReason);
        reasonText = selectedReasonObj?.label || "";
      }

      if (reasonText === "") {
        toast.error("Please select a valid reason.");
        return;
      }

      onConfirm(reasonText, isMarkCold);
    } else {
      // For purchase types or when no reason required
      onConfirm(null, isMarkCold);
    }

    // Reset states
    setSelectedReason("");
    setIsMarkCold(false);
    onOpenChange(false);
  };

  // Get thread name dynamically
  const threadName = getThreadNameFromTypeID(typeID, threadTypesData, "SnSng");

  // Determine dialog title based on thread type and stage
  const getDialogTitle = () => {
    if (typeID == 1 && currStageID > 1) {
      return `Reject ${threadName}`;
    }
    if (isInquiry) {
      return `Mark as Fake`;
    }
    return `Cancel ${threadName}`;
  };

  // Determine confirmation text
  const getConfirmationText = () => {
    if (typeID == 1 && currStageID > 1) {
      return `Please select a reason for rejecting this ${threadName.toLowerCase()}.`;
    }
    if (isInquiry) {
      return `Please select a reason for marking this ${threadName.toLowerCase()} as fake.`;
    }
    return `Please select a reason for canceling this ${threadName.toLowerCase()}.`;
  };

  // Determine button text
  const getConfirmButtonText = () => {
    if (typeID == 1 && currStageID > 1) {
      return 'Reject';
    }
    if (isInquiry) {
      return 'Mark as Fake';
    }
    return 'Yes';
  };

  const getCancelButtonText = () => {
    if (isInquiry) {
      return 'Cancel';
    }
    return 'No';
  };

  const getReasonLabel = () => {
    return showDisqlReason ? (typeID == 2 || typeID == 5) ? "Cancellation Reason" : "Disqualification Reason" : "Failure Reason";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        className="sm:max-w-md w-full gap-2"
      >
        <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
          {/* Mobile: Back Arrow with Title */}
          <div className="sm:hidden flex items-center justify-between w-full h-8">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <DialogClose
                tabIndex={-1}
                className="p-0 w-auto h-auto flex-shrink-0 flex justify-center"
                onClick={handleCancel}
              >
                <ArrowLeft className="h-6 w-6" />
                <span className="sr-only">Back</span>
              </DialogClose>
              <DialogTitle className="text-lg font-medium">
                <div className="truncate">
                  {getDialogTitle()}
                </div>
              </DialogTitle>
            </div>
          </div>

          {/* Desktop: Title */}
          <DialogTitle className="hidden sm:block text-lg md:text-xl">
            {getDialogTitle()}
          </DialogTitle>

          {/* Desktop: Close X */}
          <div className="hidden sm:flex items-center gap-2">
            <DialogClose
              className="flex-shrink-0 sm:text-text-color sm:hover:bg-gray-200"
              tabIndex={-1}
              onClick={handleCancel}
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="px-4 flex-1 overflow-y-auto max-h-[70vh]">
          <p className="text-sm text-gray-600 leading-relaxed mb-1">
            {getConfirmationText()}
          </p>

          <div className="space-y-4 mt-4">
            {/* Show disqualification reason for sales types at stage 1 */}

            {showDisqlReason && disqlRsnsOptions.length > 0 && (
              <div className="space-y-2">
                <Label required={true}>
                  {getReasonLabel()}
                </Label>
                <CustomSearchableDropdown
                  options={disqlRsnsOptions}
                  value={selectedReason}
                  onSelect={(val, option, searchValue) => {
                    if (val === 'add') {
                      handleAddReason('disqlReasons', searchValue);
                    } else {
                      setSelectedReason(val);
                    }
                  }}
                  placeholder="Select Reason"
                  displayKey="label"
                  valueKey="value"
                  label={getReasonLabel()}
                  emptyMessage="No disqualification reasons found"
                  searchEnabled={true}
                  addText="Add Reason"
                  allowAdd={true}
                />
              </div>
            )}

            {/* Show rejection reason for sales types at stage > 1 */}
            {showRejectionReason && rejectionReasonOptions.length > 0 && (
              <div className="space-y-2">
                <Label required={true}>
                  {getReasonLabel()}
                </Label>
                <CustomSearchableDropdown
                  options={rejectionReasonOptions}
                  value={selectedReason}
                  onSelect={(val, option, searchValue) => {
                    if (val === 'add') {
                      handleAddReason('rejectionReasons', searchValue);
                    } else {
                      setSelectedReason(val);
                    }
                  }}
                  placeholder="Select Reason"
                  displayKey="label"
                  valueKey="value"
                  label={getReasonLabel()}
                  emptyMessage="No failure reasons found"
                  searchEnabled={true}
                  addText="Add Reason"
                  allowAdd={true}
                />
              </div>
            )}

            {/* Mark as Cold Checkbox - Show only after reason is selected and if connection has only one open inquiry */}
            {showMarkAsCold && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="isMarkCold"
                    checked={isMarkCold}
                    onCheckedChange={(checked) => setIsMarkCold(checked)}
                  />
                  <Label htmlFor="isMarkCold" className="cursor-pointer m-0">
                    Mark as Cold
                  </Label>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2 py-2 my-2">
            <Button onClick={handleConfirm}>
              <Check className="h-4 w-4" />
              {getConfirmButtonText()}
            </Button>
            <Button variant="secondary" onClick={handleCancel}>
              <X className="h-4 w-4" />
              {getCancelButtonText()}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CancelThreadDialog;